/**
 * VipBookingCard — shown on /my-matches when the buyer has an open VIP
 * case (VIP Buying Plan or VIP Concierge). Lets them schedule their
 * included consultation call directly from the dashboard; the booking
 * lands in the super-admin dealer's Appointments tab tagged as VIP.
 *
 * Auto-opens the booking form when the URL contains ?vip=book — used by
 * the VIP purchase confirmation email CTA.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Crown, CalendarCheck2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productLabel = (pid) => {
  if (pid === 'vip_concierge') return 'VIP Concierge';
  if (pid === 'vip_buying_plan') return 'VIP Buying Plan';
  return 'VIP';
};

const VipBookingCard = ({ email, name }) => {
  const location = useLocation();
  const [status, setStatus] = useState({ has_vip: false, cases: [] });
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState('');

  const activeCase = useMemo(() => status.cases?.[0], [status]);
  const remaining = activeCase
    ? Math.max(0, (activeCase.consultations_total || 0) - (activeCase.consultations_used || 0))
    : 0;

  useEffect(() => {
    const target = (email || '').trim().toLowerCase();
    if (!target || !target.includes('@')) { setLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/my/vip-status`, { params: { email: target } });
        if (!cancelled) setStatus(res.data || { has_vip: false, cases: [] });
      } catch {
        if (!cancelled) setStatus({ has_vip: false, cases: [] });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  // Auto-open form when arriving from the VIP purchase email link.
  useEffect(() => {
    if (!loaded || !status.has_vip) return;
    const params = new URLSearchParams(location.search);
    if (params.get('vip') === 'book') setExpanded(true);
  }, [loaded, status.has_vip, location.search]);

  if (!loaded || !status.has_vip) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!date || !time) {
      setError('Please choose a preferred date and time.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/book-vip-call`, {
        email: (email || '').trim().toLowerCase(),
        name: name || 'VIP Member',
        phone: phone || undefined,
        preferred_date: date,
        preferred_time: time,
        notes: notes || undefined,
      });
      setConfirmation(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not book your call. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="mb-8 rounded-2xl overflow-hidden border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 via-violet-500/10 to-transparent"
      data-testid="vip-booking-card"
    >
      <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center">
            <Crown className="w-4 h-4 text-fuchsia-300" />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] text-fuchsia-300">{productLabel(activeCase?.product_id)}</p>
            <h2 className="text-lg font-black text-white">Book your VIP call</h2>
          </div>
        </div>
        <div className="text-right text-xs text-slate-300 shrink-0">
          <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-fuchsia-300" />
            {remaining} of {activeCase?.consultations_total || 0} calls remaining
          </div>
        </div>
      </div>

      {confirmation ? (
        <div className="p-5" data-testid="vip-booking-confirmation">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
              <CalendarCheck2 className="w-4 h-4 text-emerald-300" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">You're on the schedule.</p>
              <p className="text-xs text-slate-300 mt-1">
                Your advisor will confirm the exact time by email. You have <span className="text-fuchsia-300 font-semibold">{confirmation.consultations_remaining}</span> call{confirmation.consultations_remaining === 1 ? '' : 's'} remaining in your VIP window.
              </p>
            </div>
          </div>
        </div>
      ) : !expanded ? (
        <div className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-slate-200 flex-1 min-w-[220px]">
            Pick a time that works — your call lands in your advisor's schedule and shows up in your inbox once confirmed.
          </p>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black tracking-wide"
            data-testid="vip-booking-open-form"
          >
            <CalendarCheck2 className="w-4 h-4" /> Book my call
          </button>
        </div>
      ) : (
        <form className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={submit} data-testid="vip-booking-form">
          <label className="text-xs text-slate-300">
            Phone (optional)
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="555-0123"
              className="mt-1 w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="vip-booking-phone"
            />
          </label>
          <div />
          <label className="text-xs text-slate-300">
            Preferred date
            <input
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="mt-1 w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="vip-booking-date"
            />
          </label>
          <label className="text-xs text-slate-300">
            Preferred time
            <input
              type="text"
              required
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g. 2:00 PM CT"
              className="mt-1 w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="vip-booking-time"
            />
          </label>
          <label className="sm:col-span-2 text-xs text-slate-300">
            Anything specific you'd like to cover?
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Deal offers to review, tow questions, trade advice, etc."
              className="mt-1 w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="vip-booking-notes"
            />
          </label>
          {error && (
            <p className="sm:col-span-2 text-xs text-amber-300" data-testid="vip-booking-error">{error}</p>
          )}
          <div className="sm:col-span-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-xs text-slate-400 hover:text-white"
              data-testid="vip-booking-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black tracking-wide disabled:opacity-60"
              data-testid="vip-booking-submit"
            >
              {submitting ? 'Booking…' : 'Confirm booking'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default VipBookingCard;
